import { useState, useRef, useEffect } from 'react';

import type { CSSProperties, ReactElement } from 'react';

import inputClasses from '../Inputs.module.css';
import { useOnClickOutside } from 'Components/UI_Library/ReusableHooks/useDetectOutsideClick';
import CountryData from 'Lib/Countries.json';
import ToggleIndicator from '../../Icons/ToggleIndicator';
import CurrencyFlag from '../CurrencyFlag';

const countries = CountryData.CountryCodes;
const supportedCountries = CountryData.SupportedCountryCodes;

export type CountryInputProps = {
    marginTop?: CSSProperties['marginTop'];
    onlySupported?: boolean;
    required?: boolean;
    label?: string | ReactElement;
    optionFullWidth?: boolean;
    disabled?: boolean;
    displayCode?: boolean;
    selected?: {
        code?: string;
        name?: string;
    };
    select: (country: { name: string; countryCode: string; code: string }) => void;
};

const CountryInput = (props: CountryInputProps) => {
    const [listOpen, setListOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [visibleCountries, setVisibleCountries] = useState(countries);
    const selectBox = useRef(null);
    const dropdownRef = useRef(null);

    useOnClickOutside(dropdownRef, selectBox, () => setListOpen(false));

    useEffect(() => {
        setVisibleCountries(
            countries.filter((country) => country.name.toLowerCase().includes(searchQuery.toLowerCase()))
        );
    }, [searchQuery]);

    useEffect(() => {
        if (props.onlySupported) setVisibleCountries(supportedCountries);
    }, [props.onlySupported]);

    const renderValue = () => {
        if (!props.selected?.name) {
            return 'Select a country';
        }
        if (props.displayCode) {
            return props.selected.code;
        }

        return props.selected.name;
    };

    return (
        <div className={inputClasses.inputGroup} style={{ marginTop: props.marginTop ? props.marginTop : '', flex: 1 }}>
            {props.label && (
                <label>
                    {props.label}
                    {props.required && <span>&nbsp;&nbsp;*</span>}
                </label>
            )}
            <div
                className={inputClasses.countryInput}
                onClick={() => {
                    props.disabled ? null : setListOpen(!listOpen);
                }}
                ref={selectBox}
            >
                {props.selected && (
                    <div className={inputClasses.country}>
                        <CurrencyFlag countryCode={props.selected.code} style={{ paddingRight: 8 }} />
                        <span>{renderValue()}</span>
                    </div>
                )}
                <ToggleIndicator disabled={props.disabled} toggled={listOpen} />
            </div>
            {listOpen ? (
                <div
                    className={`${inputClasses.dropdown} }`}
                    ref={dropdownRef}
                    style={{ top: '70px', ...(props.optionFullWidth && { width: 'initial' }) }}
                >
                    <input
                        type="text"
                        placeholder="Search countries..."
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <div className={inputClasses.dropdownOptions}>
                        <div className={inputClasses.dropdownOptionsInner}>
                            {visibleCountries &&
                                visibleCountries.map((country) => (
                                    <button
                                        key={country.code}
                                        type="button"
                                        onClick={() => {
                                            props.select(country);
                                            setListOpen(false);
                                        }}
                                    >
                                        <CurrencyFlag countryCode={country.code} />
                                        <span>{country.name}</span>
                                    </button>
                                ))}
                            {visibleCountries.length === 0 && (
                                <div className={inputClasses.noResults}>No results found</div>
                            )}
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default CountryInput;
